import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiArrowLeft, FiVideo } from 'react-icons/fi';
import InfinitePracticeMini from './InfinitePracticeMini';
import ApiStore from '../../../api/ApiStore';
import { inject, observer } from 'mobx-react';
import { motion } from 'framer-motion';

const VideoPlayer = inject('store')(
  observer(({ video, onClose, store }) => {
    const [showQuiz, setShowQuiz] = useState(false);
    const [videoCompleted, setVideoCompleted] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [viewCount, setViewCount] = useState(video.views);
    const [countdown, setCountdown] = useState(3);
    const [showVideo, setShowVideo] = useState(false);
    const [viewIncremented, setViewIncremented] = useState(false);
    const videoRef = useRef(null);

    // Check if quizzes after videos are enabled in organization settings
    const quizzesEnabled =
      store.orgSettings?.configVideos?.preguntasPostVideo !== false;

    useEffect(() => {
      setShowQuiz(false);
      setShowSnackbar(false);
      setViewCount(video.views);
      setCountdown(3);
      setShowVideo(false);
      setViewIncremented(false);
    }, [video.video_id, video.views]);

    useEffect(() => {
      let timer;
      if (showSnackbar) {
        timer = setTimeout(() => {
          setShowSnackbar(false);
        }, 3000);
      }
      return () => clearTimeout(timer);
    }, [showSnackbar]);

    useEffect(() => {
      if (countdown > 0) {
        const timer = setTimeout(() => {
          setCountdown(countdown - 1);
        }, 1500);
        return () => clearTimeout(timer);
      } else if (countdown === 0) {
        setShowVideo(true);
      }
    }, [countdown]);

    // Add a new function to handle time updates
    const handleTimeUpdate = async () => {
      // Only increment the view when the user has watched at least 90% of the video
      // and we haven't already incremented the view for this session
      if (videoRef.current && !viewIncremented) {
        const percentWatched =
          (videoRef.current.currentTime / videoRef.current.duration) * 100;
        if (percentWatched >= 90) {
          setViewIncremented(true);
          setVideoCompleted(true);

          try {
            // Increment the view count in the database
            const organizationName = store.profile.organization || 'Madel';
            await ApiStore.incrementVideoView(
              video.external_id,
              organizationName
            );

            // Get the updated view count
            const updatedViewStats = await ApiStore.getVideoViews(
              video.external_id,
              organizationName
            );
            setViewCount(updatedViewStats.view_count);

            // Update the view count in the parent component
            if (video.onUpdateViewCount) {
              video.onUpdateViewCount(
                video.external_id,
                updatedViewStats.view_count
              );
            }
          } catch (error) {
            console.error('Error updating view count:', error);
          }
        }
      }
    };

    const handleVideoEnd = async () => {
      setVideoCompleted(true);

      // Only show quiz if enabled in org settings
      if (quizzesEnabled) {
        setShowQuiz(true);
        setShowSnackbar(true);

        // Scroll to the quiz section after a slight delay
        setTimeout(() => {
          const quizSection = document.getElementById('quiz-section');
          if (quizSection) {
            quizSection.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }, 800);
      }

      // If we haven't already incremented the view, do it now
      if (!viewIncremented) {
        try {
          setViewIncremented(true);

          // Increment the view count in the database
          const organizationName = store.profile.organization || 'Madel';
          await ApiStore.incrementVideoView(
            video.external_id,
            organizationName
          );

          // Get the updated view count
          const updatedViewStats = await ApiStore.getVideoViews(
            video.external_id,
            organizationName
          );
          setViewCount(updatedViewStats.view_count);

          // Update the view count in the parent component
          if (video.onUpdateViewCount) {
            video.onUpdateViewCount(
              video.external_id,
              updatedViewStats.view_count
            );
          }
        } catch (error) {
          console.error('Error updating view count:', error);
        }
      }
    };

    return (
      <div className="min-h-screen bg-gray-100 flex flex-col relative">
        <div className="flex-grow p-8 max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            {/* Back button and controls */}
            <div className="flex justify-end items-center mb-4">
              {/* <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center bg-white shadow-md px-4 py-2 rounded-full text-blue-600 hover:bg-blue-50 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                aria-label="Volver a la galería"
              >
                <FiArrowLeft className="mr-2" />
                <span className="font-medium">Volver a la galería</span>
              </motion.button> */}

              <div className="flex space-x-2">
                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center">
                  <FiVideo className="mr-1" />
                  <span className="text-sm font-medium">
                    {viewCount} visualizaciones
                  </span>
                </div>
                <button
                  onClick={onClose}
                  aria-label="Cerrar"
                  className="bg-gray-200 hover:bg-gray-300 p-2 rounded-full text-gray-600 hover:text-gray-800 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            {/* Video title */}
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {video.video_name}
            </h1>
            {video.tags && video.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {video.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-gray-200 text-gray-700 text-sm px-3 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Countdown or Video Player */}
          <div
            className="bg-black rounded-lg overflow-hidden mb-4 flex items-center justify-center"
            style={{ height: '480px', width: '100%' }}
          >
            {!showVideo ? (
              <div className="text-white text-9xl font-bold animate-pulse">
                {countdown}
              </div>
            ) : (
              <video
                ref={videoRef}
                src={video.signedUrl}
                controls
                controlsList="nodownload"
                autoPlay
                className="w-full h-full"
                onEnded={handleVideoEnd}
                onTimeUpdate={handleTimeUpdate}
              ></video>
            )}
          </div>

          {/* Video Progress */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-8">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div
                  className={`w-3 h-3 rounded-full mr-2 ${videoCompleted ? 'bg-green-500' : 'bg-yellow-500'}`}
                ></div>
                <span className="font-medium text-gray-700">
                  {videoCompleted ? 'Video completado' : 'En progreso'}
                </span>
              </div>

              {videoCompleted && !showQuiz && quizzesEnabled && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowQuiz(true);
                    // Scroll to quiz section after it's rendered
                    setTimeout(() => {
                      const quizSection =
                        document.getElementById('quiz-section');
                      if (quizSection) {
                        quizSection.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        });
                      }
                    }, 300);
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium transition-colors duration-300"
                >
                  Ver preguntas
                </motion.button>
              )}
            </div>
          </div>

          {/* Quiz Module - only show if enabled in settings */}
          {showQuiz && quizzesEnabled && (
            <div className="mt-4" id="quiz-section">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <InfinitePracticeMini videoId={video.external_id} />
              </motion.div>
            </div>
          )}
        </div>

        {/* Snackbar - only show if quizzes are enabled */}
        {showSnackbar && quizzesEnabled && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg"
          >
            <div className="flex items-center space-x-2">
              <svg
                className="w-5 h-5 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="font-medium">
                ¡Felicidades! Has desbloqueado el módulo de preguntas para este
                video.
              </p>
            </div>
          </motion.div>
        )}

        {/* Back to gallery fixed button - visible when scrolled down */}
        <motion.button
          className="fixed bottom-6 left-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 z-10"
          onClick={onClose}
          aria-label="Volver a la galería"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FiArrowLeft size={18} />
        </motion.button>
      </div>
    );
  })
);

export default VideoPlayer;
